import {
  Button,
  Inline,
  Switcher,
  Table,
  useViewport,
} from '@omnipresentgroup/design-system';
import {
  selectSalaryHistory,
  useGetSalaryHistory,
} from '@omnipresentgroup/domains';
import {
  EMPLOYEE_ID_PARAM,
  ManagerEmployeeSelector,
} from '@omnipresentgroup/shared';
import type { SalaryHistoryEntry } from '@omnipresentgroup/types';
import {
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ColumnHeaders } from './headers';
import { Columns } from './salaryChangesListColumnConfig';

const DEFAULT_PAGE_SIZE = 10;

const ERROR_STATE_TITLE = "Uh-oh, something's gone wrong";
const ERROR_STATE_DESCRIPTION =
  'Try reloading the page, and if this fails get in touch with our support team.';

const TABLE_ID = 'salary-changes-table';

const EMPTY_ARRAY: SalaryHistoryEntry[] = [];

const LOADING_ARRAY = Array.from(
  { length: 5 },
  () => ({}) as SalaryHistoryEntry,
);

type Props = {
  companyId: string;
  optionsOverride?: Omit<TableOptions<SalaryHistoryEntry>, 'data' | 'columns'>;
  columnVisibility: Partial<{
    [key in keyof typeof ColumnHeaders]: boolean;
  }>;
  filters?: Partial<Record<'employeeId', boolean>>;
};

export const SalaryChangesList = ({
  companyId,
  optionsOverride,
  columnVisibility,
  filters,
}: Props) => {
  const [searchParameters, setSearchParameters] = useSearchParams();

  const employeeId = new URLSearchParams(searchParameters).get(
    EMPLOYEE_ID_PARAM,
  ) as string;

  const DEFAULT_SORT = [
    {
      id: 'requestedAt',
      desc: true,
    },
  ];

  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(ColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORT);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const {
    data: salaryHistory,
    isLoading,
    isError,
  } = useGetSalaryHistory({
    companyId: companyId,
    enabled: Boolean(companyId),
    select: selectSalaryHistory,
    params: {
      pageSize: DEFAULT_PAGE_SIZE,
    },
    throwOnError: false,
  });

  const tableOptions: Omit<
    TableOptions<SalaryHistoryEntry>,
    'data' | 'columns'
  > = useMemo(
    () => ({
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      state: {
        columnVisibility: columnVisibilityState,
        sorting,
        columnFilters,
      },
      sortDescFirst: true,
      onSortingChange: setSorting,
      onColumnVisibilityChange: setColumnVisibilityState,
      getSortedRowModel: getSortedRowModel(),
      onColumnFiltersChange: setColumnFilters,
      getFilteredRowModel: getFilteredRowModel(),
      ...optionsOverride,
    }),
    [columnFilters, optionsOverride, sorting, columnVisibilityState],
  );

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : salaryHistory || EMPTY_ARRAY,
    columns: Columns,
    ...tableOptions,
  });

  // Update column filters based on search parameters
  useEffect(() => {
    setColumnFilters((old) => [
      ...old.filter((filter) => filter.id !== 'employeeId'),
      ...(employeeId
        ? [
            {
              id: 'employeeId',
              value: employeeId,
            },
          ]
        : []),
    ]);
  }, [employeeId, setColumnFilters]);

  const Filters = () => {
    const { isPhone, isTablet } = useViewport();
    const isStacked = useMemo(() => isPhone || isTablet, [isPhone, isTablet]);
    return (
      <Switcher
        isStacked={isStacked}
        css={{
          flexWrap: 'wrap',
        }}
      >
        {filters?.employeeId && companyId && (
          <Inline maxW={isStacked ? undefined : 400} w="stretch" stretch={1}>
            <ManagerEmployeeSelector companyId={companyId} label="Employee" />
          </Inline>
        )}
        {filters && Object.keys(filters).length > 0 && (
          <Inline align="end" justify="end">
            <Button
              variant="tertiary"
              icon="FilterOff"
              onClick={() => {
                setSearchParameters((parameters) => {
                  filters?.employeeId &&
                    companyId &&
                    parameters.delete(EMPLOYEE_ID_PARAM);
                  return parameters;
                });
              }}
            >
              Clear
            </Button>
          </Inline>
        )}
      </Switcher>
    );
  };

  return (
    <Table
      id={TABLE_ID}
      table={table}
      loading={isLoading}
      showErrorState={isError}
      filterSection={filters && <Filters />}
      errorStateProps={{
        id: `${TABLE_ID}-error`,
        title: ERROR_STATE_TITLE,
        description: ERROR_STATE_DESCRIPTION,
        callToAction: <Button onClick={window.location.reload}>Retry</Button>,
      }}
      usePagination
      useSorting
    />
  );
};
